import './Header.scss';

import NiceModal from '@ebay/nice-modal-react';
import { memo, useRef, useState } from 'react';

import { useAppSelector } from '@/app/store/hooks';
import { selectSelectedCurrency } from '@/entities/preferences';
import { selectUser, selectWallet } from '@/entities/user';
import AddSquareIcon from '@/shared/assets/icons/add-square.svg?react';
import ArrowDownIcon from '@/shared/assets/icons/arrow-down.svg?react';
import GiftIcon from '@/shared/assets/icons/gift.svg?react';
import StarIcon from '@/shared/assets/icons/star.svg?react';
import TonIcon from '@/shared/assets/icons/ton.svg?react';
import { MODALS } from '@/shared/config/modals';
import { formatCompact } from '@/shared/lib/currency';
import { Button } from '@/shared/ui/Button';
import { Dropdown } from '@/shared/ui/Dropdown';
import { PlayerAvatar } from '@/shared/ui/PlayerAvatar';

import { useWalletDropdown } from './useWalletDropdown';

const showDeposit = () => NiceModal.show(MODALS.DEPOSIT);
const showWithdraw = () => NiceModal.show(MODALS.WITHDRAW);
const showSettings = () => NiceModal.show(MODALS.SETTINGS);

function Header() {
  const user = useAppSelector(selectUser);
  const selectedCurrency = useAppSelector(selectSelectedCurrency);
  const activeWallet = useAppSelector(selectWallet(selectedCurrency));
  const activeBalance = activeWallet?.balance ?? 0;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const closeDropdown = () => setDropdownOpen(false);
  const { items, actions } = useWalletDropdown(closeDropdown, () => {
    closeDropdown();
    showWithdraw();
  });

  const displayName = user?.username
    ? user.username.length > 10
      ? user.username.slice(0, 10) + '…'
      : user.username
    : 'Player';

  return (
    <header className="header">
      <div className="header__inner">
        <button className="header__avatar-btn" onClick={showSettings} aria-label="Profile">
          <PlayerAvatar id={user?.id ?? '0'} avatar={user?.avatar} size={34} level={0} />
          <span className="header__username">{displayName}</span>
        </button>

        <div className="header__right">
          <div className="header__actions">
            <div ref={wrapperRef} className="header__wallet-wrap">
              <Button
                size="sm"
                onClick={() => setDropdownOpen((v) => !v)}
                aria-label="Select wallet"
                className="header__wallet-balance">
                {selectedCurrency === 'TON' ? (
                  <TonIcon className="header__wallet-icon" />
                ) : selectedCurrency === 'STARS' ? (
                  <StarIcon className="header__wallet-stars-icon" />
                ) : (
                  <GiftIcon className="header__gift-chip-icon" />
                )}
                <span className="header__wallet-value">{formatCompact(activeBalance)}</span>
                <ArrowDownIcon
                  className={`header__wallet-chevron ${dropdownOpen ? 'header__wallet-chevron--open' : ''}`}
                />
              </Button>
              <Dropdown
                open={dropdownOpen}
                className="header__dropdown"
                items={items}
                actions={actions}
                onClose={closeDropdown}
                triggerRef={wrapperRef}
              />
            </div>
            <Button
              variant="secondary"
              size="icon"
              onClick={showDeposit}
              aria-label="Top up"
              className="header__deposit-btn">
              <AddSquareIcon className="header__deposit-icon" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default memo(Header);
